import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  motion,
  animate,
  useMotionValue,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import {
  Section,
  Glow,
  Inner,
  ShowcaseHeader,
  Hint,
  useMediaQuery,
  clamp,
} from './shared';
import {
  CrossfadeStage,
  Marquee,
  useInView,
  Controls,
  Dots,
  Dot,
  PauseToggle,
} from './mobile';

/* Sterling - a flat slider that plays ITSELF. One screenshot sits in focus
   at full size with smaller, lightly veiled neighbours either side; the row
   slides one card at a time around an endless ring. A gated timer advances
   it, so it NEVER hijacks the page scroll. Hover holds it; the pause control
   satisfies WCAG 2.2.2; arrows, dots and the neighbour cards all move it.

   Performance: only transform and opacity animate (compositor-only), and the
   veil is an overlay whose opacity moves, never a CSS filter. */

const SIDE_SCALE = 0.8;
const GAP = 28;
const EASE = [0.22, 1, 0.36, 1];

const StageWrap = styled.div`
  position: relative;
`;

const Stage = styled.div`
  position: relative;
  height: clamp(500px, 70vh, 690px);
  /* clip sideways only, softened at the edges; top and bottom stay open so
     the card shadows fade out instead of ending in a hard edge */
  clip-path: inset(-120px 0);
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 12%, #000 88%, transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, #000 12%, #000 88%, transparent 100%);
`;

const Card = styled(motion.button)`
  position: absolute;
  left: 50%;
  top: 50%;
  /* never taller than the stage minus room for the shadow */
  width: min(clamp(214px, 24vw, 280px), calc((min(70vh, 690px) - 96px) * 1284 / 2778));
  aspect-ratio: 1284 / 2778;
  margin: 0;
  padding: 0;
  border: 1px solid ${(p) => p.$border};
  border-radius: 28px;
  overflow: hidden;
  background: ${(p) => p.$cardBg};
  box-shadow: 0 22px 44px rgba(20, 16, 10, 0.14), 0 5px 12px rgba(20, 16, 10, 0.07);
  will-change: transform, opacity;
  cursor: ${(p) => (p.$active ? 'default' : 'pointer')};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
    -webkit-user-drag: none;
    pointer-events: none;
  }
`;

const Veil = styled(motion.span)`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: ${(p) => p.$veil};
`;

const Arrow = styled.button`
  position: absolute;
  top: 50%;
  ${(p) => (p.$side === 'left' ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);
  z-index: 200;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  /* the global button rule pads and fills buttons */
  padding: 0;
  border-radius: var(--radius-pill);
  border: 1px solid ${(p) => p.$border};
  background: rgba(248, 244, 238, 0.9);
  color: ${(p) => p.$accent};
  transition: background-color 0.2s ease, transform 0.2s ease;
  &:hover { background: #fff; }
  svg { width: 16px; height: 16px; }
`;

const Chevron = ({ dir }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === 'left' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'} />
  </svg>
);

// Signed ring distance from the focus, in (-n/2, n/2]. A card crossing the
// back of the ring jumps sides only while it is fully transparent.
const ringDelta = (i, a, n) => {
  const raw = (((i - a) % n) + n) % n;
  return raw > n / 2 ? raw - n : raw;
};

function SlideCard({ img, i, n, pos, width, theme, isActive, onSelect }) {
  const d = useTransform(pos, (a) => ringDelta(i, a, n));
  const scale = useTransform(d, (v) => 1 - Math.min(Math.abs(v), 1) * (1 - SIDE_SCALE));
  // centre-to-centre: focus to first neighbour, then neighbour to neighbour
  const x = useTransform(d, (v) => {
    const a = Math.abs(v);
    const first = (width * (1 + SIDE_SCALE)) / 2 + GAP;
    const next = width * SIDE_SCALE + GAP;
    return Math.sign(v) * (Math.min(a, 1) * first + Math.max(a - 1, 0) * next) - width / 2;
  });
  const opacity = useTransform(d, (v) => clamp(0, 1, 3 - Math.abs(v)));
  const veil = useTransform(d, (v) => Math.min(Math.abs(v), 1) * 0.35);
  const zIndex = useTransform(d, (v) => 100 - Math.round(Math.abs(v) * 10));
  const visibility = useTransform(opacity, (o) => (o < 0.01 ? 'hidden' : 'visible'));

  return (
    <Card
      type="button"
      style={{ x, y: '-50%', scale, opacity, zIndex, visibility }}
      $cardBg={theme.cardBg}
      $border={theme.border}
      $active={isActive}
      tabIndex={isActive ? -1 : 0}
      aria-label={isActive ? img.alt : `Show: ${img.alt}`}
      onClick={isActive ? undefined : onSelect}
    >
      <img src={img.src} alt="" loading="eager" decoding="async" draggable="false" />
      <Veil $veil={theme.veil || '#F8F4EE'} style={{ opacity: veil }} />
    </Card>
  );
}

function AutoSlider({ images, theme, label }) {
  const ref = useRef(null);
  const cardRef = useRef(null);
  const inView = useInView(ref);
  const reduce = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [width, setWidth] = useState(260);
  const n = images.length;
  // step counts forever in either direction; the shown card is step mod n
  const [step, setStep] = useState(0);
  const pos = useMotionValue(0);
  const idx = ((step % n) + n) % n;

  // the card width is CSS-driven; read it back so the spacing tracks it
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.offsetWidth || 260);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (reduce || paused || hovered || !inView || n <= 1) return undefined;
    const id = setInterval(() => {
      if (!document.hidden) setStep((s) => s + 1);
    }, 4000);
    return () => clearInterval(id);
  }, [reduce, paused, hovered, inView, n, step]);

  useEffect(() => {
    if (reduce) { pos.set(step); return undefined; }
    const controls = animate(pos, step, { duration: 0.75, ease: EASE });
    return () => controls.stop();
  }, [step, reduce, pos]);

  // jump by the shortest way round the ring
  const go = (i) => setStep((s) => s + ringDelta(i, ((s % n) + n) % n, n));

  return (
    <StageWrap
      ref={ref}
      onPointerEnter={(e) => e.pointerType === 'mouse' && setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <Stage role="group" aria-roledescription="carousel" aria-label={label}>
        {/* invisible probe that carries the CSS card width */}
        <Card as="div" ref={cardRef} aria-hidden="true" style={{ visibility: 'hidden', transform: 'none' }} $border="transparent" />
        {images.map((img, i) => (
          <SlideCard
            key={img.src}
            img={img}
            i={i}
            n={n}
            pos={pos}
            width={width}
            theme={theme}
            isActive={i === idx}
            onSelect={() => go(i)}
          />
        ))}
      </Stage>
      <Arrow type="button" $side="left" $border={theme.border} $accent={theme.accent} aria-label="Previous screen" onClick={() => setStep((s) => s - 1)}>
        <Chevron dir="left" />
      </Arrow>
      <Arrow type="button" $side="right" $border={theme.border} $accent={theme.accent} aria-label="Next screen" onClick={() => setStep((s) => s + 1)}>
        <Chevron dir="right" />
      </Arrow>
      <p className="visually-hidden" aria-live="polite">{`Screen ${idx + 1} of ${n}: ${images[idx]?.alt || ''}`}</p>
      <Controls>
        <Dots role="group" aria-label={`${label} pagination`}>
          {images.map((img, i) => (
            <Dot
              key={img.src}
              type="button"
              $accent={theme.accent}
              $on={i === idx}
              aria-current={i === idx}
              aria-label={`Go to screen ${i + 1} of ${n}`}
              onClick={() => go(i)}
            />
          ))}
        </Dots>
        {!reduce && <PauseToggle paused={paused} setPaused={setPaused} accent={theme.accent} border={theme.border} />}
      </Controls>
    </StageWrap>
  );
}

const SterlingCoverflow = ({ eyebrow, title, subtitle, images, theme, mobileVariant = 'crossfade' }) => {
  const reduce = useReducedMotion();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const dual = mobileVariant === 'dualMarquee';

  return (
    <Section
      $bg={theme.bg}
      $borderTop={theme.borderTop}
      $borderBottom={theme.borderBottom}
      aria-label={title}
    >
      {theme.glow && <Glow $glow={theme.glow} aria-hidden="true" />}
      <Inner>
        <ShowcaseHeader
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          theme={theme}
        />
        {isDesktop ? (
          <AutoSlider images={images} theme={theme} label={`${title} screenshots`} />
        ) : dual ? (
          <Marquee
            items={images}
            theme={theme}
            label={`${title} screenshots`}
            rows={2}
            velocity={16}
          />
        ) : (
          <CrossfadeStage
            items={images}
            theme={theme}
            label={`${title} screenshots`}
            dwell={5000}
          />
        )}
        <Hint $muted={theme.muted}>
          {isDesktop
            ? 'Slides on its own - hover to hold'
            : dual
            ? 'Two rows drift on their own'
            : reduce
            ? 'Screens fade on their own'
            : 'Screens fade on their own'}
        </Hint>
      </Inner>
    </Section>
  );
};

export default SterlingCoverflow;
